import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { PaperPlaneTilt, MapPin, EnvelopeSimple, Phone } from "@phosphor-icons/react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll reply within one business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="text-white">
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="overline mb-3">Correspondence</div>
          <h1 className="font-display text-4xl sm:text-5xl">Contact the desk.</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 space-y-8">
          <p className="text-neutral-400 leading-relaxed">
            Whether you are an existing client or an allocator considering
            Madrefajona Reserved, our desk responds personally within one
            business day.
          </p>
          <ul className="space-y-5 text-sm">
            <li className="flex gap-3">
              <MapPin size={20} weight="duotone" className="text-[#E6C998]" />
              <div>
                <div className="text-white">Zurich · HQ</div>
                <div className="text-neutral-500">Bahnhofstrasse 44, 8001</div>
              </div>
            </li>
            <li className="flex gap-3">
              <EnvelopeSimple size={20} weight="duotone" className="text-[#E6C998]" />
              <div>
                <div className="text-white">desk@madrefajona.reserved</div>
                <div className="text-neutral-500">Client inquiries · 24h reply</div>
              </div>
            </li>
            <li className="flex gap-3">
              <Phone size={20} weight="duotone" className="text-[#E6C998]" />
              <div>
                <div className="text-white">+41 44 000 00 00</div>
                <div className="text-neutral-500">Members line (existing clients)</div>
              </div>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 border border-white/10 bg-[#141414] p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Name</label>
              <input
                data-testid="contact-name"
                required
                value={form.name}
                onChange={set("name")}
                className="w-full bg-black border border-white/20 px-4 py-3 focus:border-[#E6C998] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Email</label>
              <input
                data-testid="contact-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="w-full bg-black border border-white/20 px-4 py-3 focus:border-[#E6C998] focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Subject</label>
            <input
              data-testid="contact-subject"
              required
              value={form.subject}
              onChange={set("subject")}
              className="w-full bg-black border border-white/20 px-4 py-3 focus:border-[#E6C998] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Message</label>
            <textarea
              data-testid="contact-message"
              required
              value={form.message}
              onChange={set("message")}
              rows={6}
              className="w-full bg-black border border-white/20 px-4 py-3 focus:border-[#E6C998] focus:outline-none leading-relaxed"
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={busy}
            className="btn-gold px-7 py-3 rounded-none text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      </section>
    </div>
  );
}
